import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { API_URL } from "../config";

/**
 * Shape of the white-label branding/theme settings returned by
 * GET /company/settings. Mirrors the backend company_settings table.
 */
export interface IBrand {
  company_name: string;
  website_url?: string | null;
  description?: string | null;
  support_email?: string | null;
  support_phone?: string | null;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  country?: string | null;
  postal_code?: string | null;

  logo_url?: string | null;
  small_logo_url?: string | null;
  favicon_url?: string | null;
  login_logo_url?: string | null;
  login_background_url?: string | null;

  primary_color: string;
  secondary_color: string;
  sidebar_background: string;
  sidebar_text_color: string;
  topbar_background: string;
  topbar_text_color: string;
  button_color: string;
  button_text_color: string;
  card_background: string;
  background_color: string;
  text_color: string;

  border_radius: number;
  card_border_radius: number;
}

/** INTALK fallback used until the API responds (and if it ever fails). */
export const DEFAULT_BRAND: IBrand = {
  company_name: "INTALK",
  logo_url: null,
  small_logo_url: null,
  favicon_url: null,
  login_logo_url: null,
  login_background_url: null,
  primary_color: "#0079b9",
  secondary_color: "#006da8",
  sidebar_background: "#006da8",
  sidebar_text_color: "#ffffff",
  topbar_background: "#0079b9",
  topbar_text_color: "#ffffff",
  button_color: "#0079b9",
  button_text_color: "#ffffff",
  card_background: "#ffffff",
  background_color: "#f4f6f9",
  text_color: "#1e293b",
  border_radius: 8,
  card_border_radius: 12,
};

/**
 * Base URL for serving uploaded assets. Uploaded files come back as relative
 * paths (e.g. "images/abc.png"); absolute URLs are passed through untouched.
 * Mirrors the convention already used by the Company edit page.
 */
const ASSET_BASE = (
  process.env.REACT_APP_LARAVEL_API ||
  API_URL.replace(/\/api\/?$/, "") ||
  ""
).replace(/\/$/, "");

export const resolveAssetUrl = (path?: string | null): string => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || path.startsWith("data:")) return path;
  return `${ASSET_BASE}/${path.replace(/^\/+/, "")}`;
};

interface BrandContextType {
  brand: IBrand;
  /** Convenience alias for the colour tokens — `const { theme } = useBrand()`. */
  theme: IBrand;
  loading: boolean;
  /** Re-fetch after the admin saves new settings. */
  refresh: () => Promise<void>;
  /** Resolve a stored asset path to a loadable URL. */
  assetUrl: (path?: string | null) => string;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

/** Write the brand colours/radii to :root as CSS variables. */
const applyCssVariables = (brand: IBrand) => {
  const root = document.documentElement;
  const vars: Record<string, string> = {
    "--primary-color": brand.primary_color,
    "--secondary-color": brand.secondary_color,
    "--sidebar-bg": brand.sidebar_background,
    "--sidebar-text": brand.sidebar_text_color,
    "--topbar-bg": brand.topbar_background,
    "--topbar-text": brand.topbar_text_color,
    "--button-color": brand.button_color,
    "--button-text": brand.button_text_color,
    "--card-bg": brand.card_background,
    "--background-color": brand.background_color,
    "--text-color": brand.text_color,
    "--border-radius": `${brand.border_radius}px`,
    "--card-border-radius": `${brand.card_border_radius}px`,
  };
  Object.entries(vars).forEach(([k, v]) => v && root.style.setProperty(k, v));
};

const applyDocumentBranding = (brand: IBrand) => {
  if (brand.company_name) document.title = brand.company_name;

  const href = resolveAssetUrl(brand.favicon_url);
  if (href) {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = href;
  }
};

export const BrandProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [brand, setBrand] = useState<IBrand>(DEFAULT_BRAND);
  const [loading, setLoading] = useState(true);

  const fetchBrand = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/company/settings`, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      const json = await res.json();
      if (json?.data) {
        // Merge over defaults so any missing field stays sane.
        setBrand({ ...DEFAULT_BRAND, ...json.data });
      }
    } catch {
      // Keep INTALK defaults on failure — never block the app.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrand();
  }, [fetchBrand]);

  useEffect(() => {
    applyCssVariables(brand);
    applyDocumentBranding(brand);
  }, [brand]);

  const value = useMemo<BrandContextType>(
    () => ({ brand, theme: brand, loading, refresh: fetchBrand, assetUrl: resolveAssetUrl }),
    [brand, loading, fetchBrand]
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

export const useBrand = (): BrandContextType => {
  const ctx = useContext(BrandContext);
  if (ctx === undefined) {
    throw new Error("useBrand must be used within a BrandProvider");
  }
  return ctx;
};
